/**
 * SalesReports.tsx — Sales / Reports (aggregates page, §6; drill-through §7).
 *
 * One call to /v1/sales/reports (server-computed over the FULL deal set —
 * never a client derivation over the limit-200 list, D4-3). Mockup structure
 * (:749-799): 4 KPI cards, Revenue-by-month bars, Pipeline funnel (since §24
 * C-6 "Deals by stage" — the rows are a stage distribution, not a funnel), Top
 * performers. Trend deltas were OMITTED under R6-1 (no prior-period
 * snapshots existed) until 2.1b: the Total / Weighted pipeline and Avg. deal
 * size cards now carry the k-metric-delta line from the /v1/sales/reports
 * trend wire (§16.2.4, ruled §22.5; copy P2-15 — "—" plus the server's
 * reason while no honest prior exists, never a fake 0, never hidden;
 * trendDeltas.ts). The wire's fourth member, openDealCount, has no card
 * here (the count is Total pipeline's note) — it renders on the Dashboard's
 * "Open deals" card. Cycle and lead-to-win have no member on the wire (the
 * fact deltas are a later slice) and stay value-only.
 *
 * Drill-through (§7, R7-1..6): funnel rows, performer rows and revenue bars
 * drill to an IN-PAGE filtered deal table (no URL router exists — drill
 * context is component state). Fetches are server-filtered where the param
 * exists (stage / assigned_to, both live on the deals endpoint); the month
 * drill adds a client-side UTC month cut (reportsDrill.ts). The drill header
 * restates the clicked aggregate, and a shortfall against it is SAID (the
 * list caps at 200; the aggregates don't) — never silently rendered as
 * reconciled. Since §23 (D-1..D-5) FOUR of the five KPI cards drill through
 * the same shape: Total pipeline and Avg. deal size to the composite 'open'
 * population (the mean restated in the header — it reconciles once count
 * and sum do), Weighted pipeline to the 'weighted' kind (a per-row Weighted
 * column whose Σ is the card, exactly), Avg. sales cycle to the 'cycle' kind
 * (a Days column; rows without a close stamp dropped and counted). The ONE
 * card that does NOT drill is Lead-to-win rate: its denominator
 * (allDealCount) is not on the /reports wire, and by D-6 it rides the next
 * backend slice that touches /reports — never a numerator-only drill, which
 * would show a ratio drilling to half of itself. A non-drillable card is a
 * plain div and looks no different at rest (D-8, R7-5). Bars are
 * pointer-only (R7-3, recharts limitation — known gap).
 * Resting render is pixel-identical to the pre-drill page (R7-5): buttons
 * carry the former row classes; affordances are hover/focus-only.
 *
 * Data honesty: nullable KPIs (cycle before a first win, lead-to-win before
 * a first deal) render as an em dash, never a fake zero. "Revenue" is
 * closed-won deal value, not invoiced revenue — the subtitle says so.
 *
 * The drill VIEW itself lives in ReportsDrillView.tsx since §33 W-5 (moved,
 * not forked — SB-12's instruction, taken when the Forecast tab became the
 * third page to render it); this page keeps only its drill STATE.
 *
 * Tabs (2.3b, U-1/U-2): "Reports | Forecast" through the shared TabStrip —
 * the Settings page's tablist, lifted, not a second pattern. The strip
 * renders ABOVE the drill early-return so a reader who drilled from Reports
 * can reach Forecast without going back first; the cost is that the drill
 * baseline re-records. Each tab owns its own read: a failed /reports fetch
 * never hides the Forecast tab, and vice versa.
 *
 * P1: colors/sizes flow from src/theme/tokens.ts (canonical P1 design system).
 */
import { useState } from 'react'
import { SectionTitle, Avatar, LoadingState, ErrorState, EmptyState, TabStrip } from '../../DashboardShared'
import { ForecastTab } from './SalesForecast'
import { ReportBuilderTab } from './SalesReportBuilder'
import { SalesMetric } from './SalesDashboard'
import { ReportsDrillView } from './ReportsDrillView'
import { useAsync, fetchSalesReports } from '../../api'
import { money } from '../../../../lib/money'
import { RevenueByMonthChart } from './RevenueByMonthChart'
import { DealsByStageRows } from './DealsByStageRows'
import { type ReportsDrill } from './reportsDrill'
import { dayCount } from '../../../../lib/plural'
import { fencedCount, reportsBeltLine, unownedWonLine, overridePointerLine, excludedSuffix } from './fenceCopy'
import { absentMembers, deltaLine, historyFootnote } from './trendDeltas'

// Takes integer PENCE (private copy, the SalesDashboard/finance precedent).
function fmtMoney(pence: number) {
  const n = pence / 100
  if (n >= 1_000_000) return `£${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `£${(n / 1_000).toFixed(0)}k`
  return `£${n.toFixed(0)}`
}

// ── The page: title, tabs, then the active tab ────────────────────────────────

type ReportsTab = 'reports' | 'forecast' | 'builder'
const REPORTS_TABS = [{ id: 'reports', label: 'Reports' }, { id: 'forecast', label: 'Forecast' }, { id: 'builder', label: 'Builder' }] as const
const TAB_SUBTITLE: Record<ReportsTab, string> = {
  reports: 'Sales performance overview',
  forecast: 'Targets, attainment and pipeline by forecast category',
  // 2.5 (§16.7): one object, one group-by, one measure — the bounded builder.
  builder: 'Ask one question of one object — grouped one way, measured one way',
}

export function SalesReports({ onSelectDeal }: { onSelectDeal?: (id: string) => void }) {
  const [tab, setTab] = useState<ReportsTab>('reports')
  return (
    <div className="page-enter flex flex-col gap-4">
      <SectionTitle title="Reports" subtitle={TAB_SUBTITLE[tab]} />
      <TabStrip<ReportsTab> label="Report views" tabs={REPORTS_TABS} active={tab} onSelect={setTab} />
      {tab === 'forecast' ? <ForecastTab onSelectDeal={onSelectDeal} /> : tab === 'builder' ? <ReportBuilderTab /> : <ReportsBody onSelectDeal={onSelectDeal} />}
    </div>
  )
}

// ── The report ────────────────────────────────────────────────────────────────

function ReportsBody({ onSelectDeal }: { onSelectDeal?: (id: string) => void }) {
  const { data, loading, error } = useAsync(fetchSalesReports, [])
  const [drill, setDrill] = useState<ReportsDrill | null>(null)

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} />

  // U-2: the drill renders INSIDE the tab strip, never instead of it.
  if (drill) return <ReportsDrillView drill={drill} onBack={() => setDrill(null)} onSelectDeal={onSelectDeal} />

  const r = data!

  // 2.1b: each delta is the wire's member against the live figure on the
  // SAME payload (§22.6: the screen takes the difference); the avg member
  // compares against nothing when no deal is open, exactly as the card's own
  // value does. A pre-2.1b backend has no `deltas` → no line renders.
  const d = r.deltas
  // RULED 2026-09-26: one footnote, the spec's sentence with the ledger's
  // date — the server's reason sentence is not rendered (trendDeltas.ts).
  const footnote = historyFootnote(r.historySince, absentMembers(d) > 0)
  // §23 D-2..D-6: each card names the ReportsDrill behind its figure, or none.
  // Total pipeline and Avg. deal size share the composite 'open' drill (the
  // Dashboard's, lifted): two figures, one population; the avg variant
  // carries the server mean for its header. Weighted and cycle drill to
  // their own kinds. A drill exists only while its figure does (a "—" card
  // has nothing behind it). Lead-to-win: NO drill — see the file header.
  const openDrill: ReportsDrill = { kind: 'open', openDeals: r.openDealCount, pipelinePence: r.totalPipelinePence }
  const overridePointer = overridePointerLine(r.overriddenOpenCount, r.overrideGapPence)
  const beltLine = reportsBeltLine(r.excludedNonGbp)
  const unownedLine = unownedWonLine(r.unownedWon)
  const KPIS: { label: string; value: string; hint: string; delta?: ReturnType<typeof deltaLine>; drill?: ReportsDrill }[] = [
    // §26 F-1: openDealCount is the FENCED count — the deals that produced
    // the figure — with "(+k excluded)" from the wire's own excluded count
    // (F-1d); F-3: the hint names WHICH value this is (the deals' own).
    { label: 'Total pipeline', value: fmtMoney(r.totalPipelinePence), hint: `${fencedCount(r.openDealCount, 'open deal', r.openExcludedNonGbp)} · deals' own value`,
      delta: deltaLine(d?.pipelinePence, r.totalPipelinePence, fmtMoney), drill: openDrill },
    // 2.2 (P2-8): the effective-probability weighting; absent on a pre-2.2
    // backend → "—", never a fake 0.
    { label: 'Weighted pipeline', value: r.weightedPipelinePence != null ? fmtMoney(r.weightedPipelinePence) : '—', hint: 'by effective probability',
      delta: deltaLine(d?.weightedPipelinePence, r.weightedPipelinePence ?? null, fmtMoney),
      drill: r.weightedPipelinePence != null ? { kind: 'weighted', openDeals: r.openDealCount, pipelinePence: r.totalPipelinePence, weightedPence: r.weightedPipelinePence } : undefined },
    { label: 'Avg. deal size', value: r.openDealCount > 0 ? fmtMoney(r.avgDealSizePence) : '—', hint: 'mean of open deals',
      delta: deltaLine(d?.avgDealSizePence, r.openDealCount > 0 ? r.avgDealSizePence : null, fmtMoney),
      drill: r.openDealCount > 0 ? { ...openDrill, avgPence: r.avgDealSizePence } : undefined },
    // The card shows a ROUNDED whole number, so it pluralises the number it
    // shows: 1.2491 → "1 day" (§23.3.4's "1 days" nit); the drill's decimal
    // mean ("1.2 days") is a measurement and keeps the plural.
    { label: 'Avg. sales cycle', value: r.avgSalesCycleDays != null ? dayCount(Math.round(r.avgSalesCycleDays)) : '—', hint: 'won deals, created → closed',
      drill: r.avgSalesCycleDays != null && r.wonCount != null ? { kind: 'cycle', wonCount: r.wonCount, meanDays: r.avgSalesCycleDays } : undefined },
    { label: 'Lead-to-win rate', value: r.leadToWinRatePct != null ? `${Math.round(r.leadToWinRatePct)}%` : '—', hint: 'won ÷ all deals created' },
  ]

  return (
    <>
      {/* 5 KPI cards — .k-metric; the three pipeline cards carry the 2.1b
          delta line (absent → "—" + the server's reason, P2-15), the two fact
          cards stay value-only (no member on the wire); weighted pipeline
          added by 2.2. §23: four drill (a real <button>, SB-12's shape), the
          Lead-to-win card stays a div (D-6) and looks no different (D-8). */}
      <div className="grid grid-cols-5 gap-3">
        {KPIS.map(k => <SalesMetric key={k.label} label={k.label} value={k.value} note={k.hint} delta={k.delta}
          onClick={k.drill ? () => setDrill(k.drill!) : undefined} />)}
      </div>
      {/* §26 F-3: when an override stands, say the two views differ and that
          both are correct (B-11) — the wire has the number; the page no
          longer withholds it. Absent while no open deal carries one. */}
      {overridePointer && <p className="text-12 text-ink-muted" data-testid="override-pointer">{overridePointer}</p>}
      {/* §26 F-1c: the belt at PAGE level — before this the only disclosure
          of an excluded deal was one click deep in a drill. Absent at 0. */}
      {beltLine && <p className="text-12 text-ink-muted" data-testid="reports-belt">{beltLine}</p>}
      {/* P2-15: the history floor, from the ledger — never hidden while the
          wire carries it; absent only on a pre-2.1b backend. */}
      {footnote && <p className="text-12 text-ink-muted">{footnote}</p>}

      <div className="grid grid-cols-2 gap-4">
        {/* Revenue by month — closed-won value, 6 zero-filled months, the
            SHARED chart (§24 C-8: RevenueByMonthChart, the k-chart contract
            with C-2..C-5 + C-9 inside it). Bar click drills to that month's
            won deals — POINTER-ONLY (R7-3: recharts bars aren't
            keyboard-reachable; known, flagged gap). */}
        <div className="k-card">
          <div className="k-card-head">
            <div>
              <div className="k-card-title">Revenue by month</div>
              <div className="k-card-sub">Closed-won value, last 6 months</div>
            </div>
          </div>
          <div className="p-4">
            <RevenueByMonthChart data={r.revenueByMonth}
              onBarClick={m => setDrill({ kind: 'month', month: m.month, wonPence: m.wonPence })} />
          </div>
        </div>

        {/* Deals by stage — per-stage counts on the fixed 5-row spine, the
            SHARED rows (§24 C-6 / C-7: DealsByStageRows). Renamed from
            "Pipeline funnel": the rows count a STOCK (deals in each stage
            now), so this is a stage distribution wearing a funnel's name —
            the Dashboard already called it by its right one. Rows are real
            buttons (keyboard + focus ring) that drill to the stage's deals. */}
        <div className="k-card">
          <div className="k-card-head">
            <div>
              <div className="k-card-title">Deals by stage</div>
              <div className="k-card-sub">Open deals per stage</div>
            </div>
          </div>
          <DealsByStageRows funnel={r.funnel}
            onDrill={row => setDrill({ kind: 'stage', stage: row.stage, label: row.label, count: row.count })} />
        </div>
      </div>

      {/* Top performers — grouped by deal owner, pipeline value desc. Rows
          drill to ALL that rep's deals (R7-2); keyboard-reachable. */}
      <div className="k-card">
        <div className="k-card-head">
          <div>
            <div className="k-card-title">Top performers</div>
            <div className="k-card-sub">{`By pipeline value owned · won this quarter = closed since ${r.quarterStart}`}</div>
          </div>
        </div>
        {/* The E360-2 unlinked bucket, the nonGbpNote idiom: unlinked owners
            are LISTED below like any other — this only says how many deals
            still carry a name rather than a person. Absent when none do. */}
        {/* §26 F-2b: the THIRD population — won by nobody — said here, beside
            the unlinked line: it is in the revenue chart and in no row
            below, so performers + unlinked-named + unowned = the quarter. */}
        {unownedLine && (
          <div className="px-5 py-2 text-12 text-ink-muted border-b border-edge-row" data-testid="unowned-won">{unownedLine}</div>
        )}
        {r.unlinkedOwnerDeals > 0 && (
          <div className="px-5 py-2 text-12 text-ink-muted border-b border-edge-row">
            {r.unlinkedOwnerDeals} {r.unlinkedOwnerDeals === 1 ? 'deal is' : 'deals are'} owned by a name that isn't linked to an employee — {r.unlinkedOwnerDeals === 1 ? 'it groups' : 'they group'} under that name below.
          </div>
        )}
        {r.topPerformers.length === 0 ? (
          <EmptyState message="No performers to rank yet" hint="Deals need an owner before they can be grouped here." />
        ) : (
          <table className="closr-table sales-table w-full">
            <thead>
              <tr>
                <th className="text-left">Rep</th>
                <th className="text-left">Open deals</th>
                <th className="text-left">Pipeline value</th>
                {/* §26 F-2a: the population is NAMED — the owner pair, not the
                    Forecast grid's linked employees. */}
                <th className="text-left">Won this quarter (by owner)</th>
              </tr>
            </thead>
            <tbody>
              {r.topPerformers.map(p => {
                const go = () => setDrill({
                  kind: 'rep', rep: p.rep, employeeId: p.employeeId, openDeals: p.openDeals,
                  pipelinePence: p.pipelinePence, wonThisQuarterPence: p.wonThisQuarterPence,
                  quarterStart: r.quarterStart,
                })
                return (
                  <tr key={p.rep} tabIndex={0} onClick={go}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go() } }}
                    className="cursor-pointer hover:bg-surface-row-hover transition-colors focus:outline-none ">
                    <td>
                      <div className="flex items-center gap-2 text-13 font-medium text-ink-primary">
                        <Avatar name={p.rep} size={22} />{p.rep}
                      </div>
                    </td>
                    <td className="text-13 text-ink-body">{p.openDeals}{excludedSuffix(p.excludedNonGbp) && <span className="text-11 text-ink-faint"> {excludedSuffix(p.excludedNonGbp)}</span>}</td>
                    <td className="text-13 font-semibold text-ink-primary">{money(p.pipelinePence / 100)}</td>
                    <td className="text-13 text-ink-body">{money(p.wonThisQuarterPence / 100)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </>
  )
}
